/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactSVG from 'react-svg';
import { AddCircleSharp, Textsms, Close, Edit } from '@material-ui/icons';
import { CircularProgress, Tooltip } from '@material-ui/core';
import {
  usePlayRecord,
  useTryToCall,
  useNavigation,
  useFormatPhoneNumber,
} from '~/hooks';
import * as lib from '~/dependencies';
import api from '~/services';
import * as actions from '~/store/actions';
import * as NuacomComponent from '~/components';
import { DetailsInsights } from '~/components/Integrations';
import messagesToast from '~/validations/messagesNotifications';

import {
  CallIcon,
  PlayIcon,
  NoteIcon,
  TagIcon,
  PlayIconOff,
  StopIcon,
} from '~/assets/svgIcons';
import {
  log,
  isLocalExtension,
  // isInbound,
  eventTracker,
  formatCallDate,
} from '../../helpers';

import { isSMSCapableNumber } from '../Conversations/components/RecipientsSelector/utils';

import {
  Container,
  Space,
  DateExtended,
  BoxInformations,
  Row,
  BoxData,
  IconButtonComponent,
  Wrapper,
  DottedDivisor,
  AddContactButton,
  BoxDirection,
  DirectionLabel,
  Content,
  AddTagsNotesButton,
  Label,
  // RecentContainer,
} from './styles';

export default function CallDetails() {
  // HOOKS
  const [{ callRecord }, setPlayRecord] = usePlayRecord();
  const tryToCall = useTryToCall();
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();
  const location = lib.useLocation();
  const navigate = useNavigation();
  const { formatDisplayPhoneNumber } = useFormatPhoneNumber();

  // STATES
  const [playerLoading, setPlayerLoading] = useState(false);
  const [addCallNotes, setAddCallNotes] = useState(false);
  const [addTags, setAddTags] = useState(false);
  const [player, setPlayer] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(location.pathname);

  // SELECTORS
  const callActive = useSelector(state => state.phone?.callInProgress);
  const userExtension = useSelector(state => state.auth?.userExtension);
  const isIntegrated = useSelector(state => state.integrations.isIntegrated);

  const details = useSelector(state =>
    state.call.callDetails ? state.call.callDetails[0] : {}
  );

  // EFFECT'S
  useEffect(() => {
    if (location.pathname !== currentLocation) {
      dispatch(actions.resetCallDetails());
    }
    setCurrentLocation(location.pathname);
  }, [currentLocation, dispatch, location]);

  async function playRecord(action, callId) {
    if (action && !playerLoading) {
      setPlayerLoading(true);
      await setPlayRecord(callId);
      setPlayerLoading(false);
      setPlayer(true);
    } else {
      setPlayer(false);
    }
  }

  function setNumber(value) {
    dispatch(actions.numberContactToTransfer(value));
  }

  async function deleteCallRecording(recordCallId) {
    try {
      setPlayer(false);
      await api.calls.delete.record(recordCallId);
      dispatch(actions.updateCallRecordingStatus(recordCallId));

      lib.ToastMessage(
        'success',
        messagesToast().CALLS.callRecording.delete.success
      );
    } catch (err) {
      log.error('Failed to delete call recorder.', err);
      lib.ToastMessage(
        'error',
        messagesToast().CALLS.callRecording.delete.error
      );
    }
  }

  function acceptDeleteCallRecording(action) {
    if (action) {
      deleteCallRecording(details.callId);
      setOpenModal(false);
    } else {
      setOpenModal(false);
    }
  }

  function playCallRecording(playerState, detailsObj) {
    if (detailsObj?.recording) {
      playRecord(playerState, detailsObj.callId);
    }
    eventTracker(events => events.PlayCallRecording);
  }

  function handleDelete() {
    setOpenModal(true);
  }

  function handleSendSMS(destNumber, destName, contactId) {
    navigate.smsSenderView({
      ...(contactId ? { id: contactId } : {}),
      name: destName,
      mobile: destNumber,
    });
  }

  function handleClose() {
    dispatch(actions.resetCallDetails());
  }

  const logAdvancedCallDetails = () => {
    log.debug('Call details ', JSON.parse(JSON.stringify(details)));
  };

  const playStopIcon = player ? StopIcon : PlayIcon;

  return (
    <Wrapper id="callDetails">
      <Container>
        <BoxDirection onClick={logAdvancedCallDetails}>
          <Content>
            <ReactSVG src={details.icon} className="icon" />
            <DirectionLabel>
              {details.directionLabel &&
                t('components.callDetails.direction', {
                  direction:
                    details.directionLabel.charAt(0).toUpperCase() +
                    details.directionLabel.slice(1),
                })}
            </DirectionLabel>
          </Content>
          <Close onClick={handleClose} />
        </BoxDirection>
        <Space height={30} />
        <DateExtended className="call-date">
          <span>{formatCallDate(details.callDate)}</span>
        </DateExtended>
        <Space height={10} />
        <BoxInformations>
          <Row>
            <BoxData>
              <Label name="from">{t('components.callDetails.from')}</Label>
              <span />
            </BoxData>
            <BoxData center={details.fromName}>
              {details.fromName && (
                <span className="contact-name">{details.fromName}</span>
              )}
              <span
                className="contact-number"
                data-test-id={details.from + details.fromExt}>
                {details.fromName?.toLowerCase() === 'anonymous'
                  ? details.fromExt
                  : formatDisplayPhoneNumber(details.from) || details.fromExt}
              </span>
              {!details.fromName && <span />}
            </BoxData>
            {details.from !== userExtension && !isLocalExtension(details.from) && (
              <BoxData>
                <Tooltip
                  title={
                    details.fromContactId
                      ? t('components.callDetails.editContact')
                      : t('components.callDetails.addToContacts')
                  }>
                  <AddContactButton>
                    <lib.Link
                      className={{ fontSize: '0px !important' }}
                      to={{
                        pathname: `/app/addNewContact`,
                        state: {
                          ...(!details.fromContactId && {
                            landLine: details.from,
                            fromCallDetails: true,
                          }),
                          ...(details.fromContactId &&
                            details.fromContactId && {
                              id: details.fromContactId,
                              edit: true,
                            }),

                          ...(!details.fromContactId && {
                            landLine: details.from,
                            edit: true,
                          }),
                        },
                        ...(details.fromContactId && {
                          hash: '#edit',
                        }),
                      }}>
                      {details.fromContactId ? (
                        <Edit className="icon-edit" />
                      ) : (
                        <AddCircleSharp className="icon-add" />
                      )}
                    </lib.Link>
                  </AddContactButton>
                </Tooltip>
              </BoxData>
            )}
            {isSMSCapableNumber(details.internationalFrom) && (
              <BoxData>
                <Tooltip title={t('screens.callDetails.sendSMS')}>
                  <IconButtonComponent
                    className="icon-sms"
                    data-test-id="btn-sms-open"
                    onClick={() =>
                      handleSendSMS(
                        details?.internationalFrom || details.fromLocal,
                        details.toContactName ||
                          details.fromLocal ||
                          details.internationalFrom,
                        details.fromContactId
                      )
                    }>
                    <Textsms className="icon-sms-svg" />
                  </IconButtonComponent>
                </Tooltip>
              </BoxData>
            )}
            <BoxData>
              <IconButtonComponent
                id="test-call-details-from"
                aria-label="Call"
                onClick={() => {
                  handleClose();
                  if (callActive) {
                    setNumber(details.internationalFrom || details.from);
                  } else {
                    tryToCall(details.internationalFrom || details.from, {
                      name: details.fromName,
                      contactId: details.fromContactId,
                    });
                  }
                }}>
                <ReactSVG src={CallIcon} className="icon" />
              </IconButtonComponent>
            </BoxData>
          </Row>
          <Row>
            <BoxData>
              <Label name="to">{t('components.callDetails.to')}</Label>
              <span />
            </BoxData>
            <BoxData>
              {details.toName && (
                <span className="contact-name">{details.toName}</span>
              )}
              <span className="contact-number">
                {details.toName?.toLowerCase() === 'anonymous'
                  ? details.toExt
                  : formatDisplayPhoneNumber(details.to) || details.toExt}
              </span>
              {!details.toName && <span />}
            </BoxData>
            {details.to !== userExtension && !isLocalExtension(details.to) && (
              <BoxData>
                <AddContactButton>
                  <Tooltip
                    title={
                      details.toContactId
                        ? t('components.callDetails.editContact')
                        : t('components.callDetails.addToContacts')
                    }>
                    <lib.Link
                      className={{ fontSize: '0px !important' }}
                      to={{
                        pathname: `/app/addNewContact`,
                        state: {
                          ...(!details.toContactId && {
                            landLine: details.to,
                            fromCallDetails: true,
                          }),
                          ...(details.toContactId &&
                            details.toContactId && {
                              id: details.toContactId,
                              edit: true,
                            }),

                          ...(!details.toContactId && {
                            landLine: details.to,
                            edit: true,
                          }),
                        },
                        ...(details.toContactId && {
                          hash: '#edit',
                        }),
                      }}>
                      {details.toContactId ? (
                        <Edit className="icon-edit" />
                      ) : (
                        <AddCircleSharp className="icon-add" />
                      )}
                    </lib.Link>
                  </Tooltip>
                </AddContactButton>
              </BoxData>
            )}
            {isSMSCapableNumber(details.internationalTo) && (
              <BoxData>
                <Tooltip title={t('screens.callDetails.sendSMS')}>
                  <IconButtonComponent
                    className="icon-sms"
                    data-test-id="btn-sms-open"
                    onClick={() =>
                      handleSendSMS(
                        details?.internationalTo || details.toLocal,
                        details.toContactName ||
                          details.toLocal ||
                          details.internationalTo,
                        details.toContactId
                      )
                    }>
                    <Textsms className="icon-sms-svg" />
                  </IconButtonComponent>
                </Tooltip>
              </BoxData>
            )}
            <BoxData>
              <IconButtonComponent
                id="test-call-details-to"
                aria-label="Call"
                onClick={() => {
                  handleClose();
                  if (callActive) {
                    setNumber(details.internationalTo || details.to);
                  } else {
                    tryToCall(details.internationalTo || details.to, {
                      name: details.toName,
                      contactId: details.toContactId,
                    });
                  }
                }}>
                <ReactSVG src={CallIcon} className="icon" />
              </IconButtonComponent>
            </BoxData>
          </Row>
          {details.direction === 'outbound' && (
            <Row>
              <BoxData>
                <Label name="caller-id">
                  {t('components.callDetails.callerId')}
                </Label>
                <span />
              </BoxData>
              <BoxData>
                {details.fromName && (
                  <span className="contact-name" data-test-id="from-caller-id">
                    {details.fromName}
                  </span>
                )}
                <span className="contact-number">
                  {formatDisplayPhoneNumber(
                    details.internationalFrom || details.fromLocal
                  )}
                </span>
                {!details.fromName && <span />}
              </BoxData>
            </Row>
          )}
          {details && details.callInQueue ? (
            <Row>
              <BoxData>
                <Label name="queue-name" aria-label={details.callInQueue}>
                  {t('components.callDetails.queue')}:
                </Label>
                <span />
              </BoxData>
              <BoxData style={{ marginLeft: 5 }}>
                <span className="queue-name">
                  #{details.callInQueue}-{details.callInQueueName}
                </span>
              </BoxData>
            </Row>
          ) : null}
          {details.direction === 'inbound' && details.answeredBy && (
            <Row>
              <BoxData>
                <Label name="answered-by">
                  {t('components.callDetails.answeredBy')}
                </Label>
                <span />
              </BoxData>
              <BoxData style={{ marginLeft: 5 }}>
                {details.toExtName && (
                  <span
                    className="contact-name"
                    data-test-id="answered-by-ext-name">
                    {details.toExtName}
                  </span>
                )}
                <span
                  className="contact-number"
                  data-test-id="answered-by-ext-number">
                  {details.toExt}
                </span>
                {!details.toExtName && <span />}
              </BoxData>
              <BoxData>
                <IconButtonComponent
                  aria-label="Call"
                  onClick={() => {
                    handleClose();
                    if (callActive) {
                      setNumber(details.answeredBy);
                    } else {
                      tryToCall(details.answeredBy);
                    }
                  }}>
                  <ReactSVG src={CallIcon} className="icon" />
                </IconButtonComponent>
              </BoxData>
            </Row>
          )}
          <Row style={{ border: player && '0' }}>
            <BoxData>
              <Label name="duration">
                {t('components.callDetails.duration')}
              </Label>
              <span />
            </BoxData>
            <BoxData>
              <span className="contact-number">{details.discriptiveTime}</span>
              <span />
            </BoxData>
            <BoxData>
              {!playerLoading && (
                <IconButtonComponent
                  aria-label="Play call recording"
                  onClick={() => playCallRecording(!player, details)}
                  disabled={!details.recording}>
                  <ReactSVG
                    src={details.recording ? playStopIcon : PlayIconOff}
                    className="icon"
                  />
                </IconButtonComponent>
              )}
              {playerLoading && (
                <div className="holderDiv">
                  <CircularProgress />
                  <IconButtonComponent
                    aria-label="Call"
                    onClick={() =>
                      details.recording
                        ? playRecord(!player, details.callId)
                        : {}
                    }
                    disabled={!details.recording}>
                    <ReactSVG src={StopIcon} className="icon-2" />
                  </IconButtonComponent>
                </div>
              )}
            </BoxData>
          </Row>

          {player && !!callRecord && (
            <>
              <NuacomComponent.Player
                audio={callRecord}
                onDelete={handleDelete}
              />
              <div style={{ borderTop: '1px dotted #dfdfdf' }} />
            </>
          )}

          {isIntegrated && <DetailsInsights details={details} />}

          <Row className="actions-container">
            <AddTagsNotesButton
              action="add-tags"
              type="button"
              onClick={() => {
                setAddTags(true);
                eventTracker(events => events.HistoryAddTagClicked);
              }}>
              <ReactSVG src={TagIcon} className="icon" />
              <span>{t('components.callDetails.addTag')}</span>
            </AddTagsNotesButton>

            <AddTagsNotesButton
              type="button"
              action="add-notes"
              onClick={() => {
                setAddCallNotes(true);
                eventTracker(events => events.HistoryAddNoteClicked);
              }}>
              <ReactSVG src={NoteIcon} className="icon" />
              <span>{t('components.callDetails.addNote')}</span>
            </AddTagsNotesButton>
          </Row>
        </BoxInformations>
      </Container>

      {details.callId && (
        <>
          <NuacomComponent.SendNotes
            callId={details.callId}
            open={addCallNotes}
            onClose={setAddCallNotes}
          />

          <Space height={16} />
          <NuacomComponent.ShowTags
            callId={details.callId}
            sipCallId={details.sipCallId}
            originalSipCallId={details.originalSipCallId}
          />
          {/* <DottedDivisor /> */}
          <Space height={3} />

          <NuacomComponent.AddTags
            callId={details.callId}
            sipCallId={details.sipCallId}
            originalSipCallId={details.originalSipCallId}
            open={addTags}
            onClose={setAddTags}
            callInfo={details}
          />

          <DottedDivisor />
          <Space height={15} />

          <NuacomComponent.ShowNotes
            callId={details.callId}
            sipCallId={details.sipCallId}
            originalSipCallId={details.originalSipCallId}
          />
          {/* Disable as result of NWA-1993 */}
          {/* <RecentContainer>
              <NuacomComponent.RecentCallHistory
                phoneNumber={
                  isInbound(details.direction)
                    ? details.internationalFrom
                    : details.internationalTo
                }
              />
            </RecentContainer> */}
        </>
      )}

      <NuacomComponent.ConfirmationBox
        setOpenModal={setOpenModal}
        openModal={openModal}
        handleAction={acceptDeleteCallRecording}
      />

      <Space height={8} />
    </Wrapper>
  );
}
